import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Renderer2 } from '@angular/core';

/**
 * Draws a horizontal scroll track (`.scroll-foot`, with a `.scroll-foot-thumb`) as the last child
 * of a horizontally scrolling container, for phones whose native scrollbar can't be styled or
 * relied on — iOS Safari ignores `::-webkit-scrollbar`. The track is only shown while the content
 * overflows by more than `MIN_OVERFLOW` px and has at least one data row; the thumb's width is the
 * visible fraction of the content and it follows `scrollLeft`.
 *
 * The directive only builds and positions the elements. Their look, the breakpoint they show at
 * and the suppression of the native bar belong to the host page's styles.
 */
@Directive({
    selector: '[rmsScrollFoot]',
    standalone: false
})
export class ScrollFootDirective implements AfterViewInit, OnDestroy {
  /**
   * Overflow at or below this is rounding, not content: an empty table measured 391px in a 390px
   * box, which drew a full-width thumb with nothing to scroll.
   */
  public static readonly MIN_OVERFLOW = 4;

  private foot: HTMLElement;
  private thumb: HTMLElement;
  private resizeObserver: ResizeObserver;
  private removeScrollListener: () => void;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone
  ) { }

  ngAfterViewInit(): void {
    const host = this.elementRef.nativeElement;
    this.foot = this.renderer.createElement('div');
    this.thumb = this.renderer.createElement('div');
    this.renderer.addClass(this.foot, 'scroll-foot');
    this.renderer.addClass(this.thumb, 'scroll-foot-thumb');
    this.renderer.setAttribute(this.foot, 'aria-hidden', 'true');
    this.renderer.appendChild(this.foot, this.thumb);
    this.renderer.appendChild(host, this.foot);

    // Scroll and resize fire continuously; nothing here needs change detection.
    this.ngZone.runOutsideAngular(() => {
      this.removeScrollListener = this.renderer.listen(host, 'scroll', () => this.update());

      if (typeof ResizeObserver !== 'undefined') {
        // Watching the content as well as the host catches width changes from re-filtered rows.
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(host);
        Array.from(host.children)
          .filter(child => child !== this.foot)
          .forEach(child => this.resizeObserver.observe(child));
      }
    });

    this.update();
  }

  ngOnDestroy(): void {
    this.removeScrollListener?.();
    this.resizeObserver?.disconnect();
  }

  /** Shows the track only while the content really overflows, and sizes and places the thumb. */
  public update(): void {
    if (!this.foot) {
      return;
    }

    const { scrollWidth, clientWidth, scrollLeft } = this.elementRef.nativeElement;
    const overflow = scrollWidth - clientWidth;

    if (overflow <= ScrollFootDirective.MIN_OVERFLOW || !this.hasDataRows()) {
      this.renderer.setStyle(this.foot, 'display', 'none');
      return;
    }

    this.renderer.removeStyle(this.foot, 'display');
    const thumbWidth = clientWidth * clientWidth / scrollWidth;
    const offset = (scrollLeft / overflow) * (clientWidth - thumbWidth);
    this.renderer.setStyle(this.thumb, 'width', `${thumbWidth}px`);
    this.renderer.setStyle(this.thumb, 'transform', `translateX(${offset}px)`);
  }

  /**
   * Whether the table has any rows to scroll to. A wide header alone overflows, but an empty
   * state is a single full-width `colspan` cell, so only rows with more than one cell count.
   */
  private hasDataRows(): boolean {
    return Array.from(this.elementRef.nativeElement.querySelectorAll('tbody tr'))
      .some(row => row.children.length > 1);
  }
}
